<script setup lang="ts">
const props = withDefaults(defineProps<{
  /** Heading shown above the form. */
  title?: string
  /** Optional intro line under the heading. */
  subtitle?: string
  /** Submit button label. */
  submitLabel?: string
}>(), {
  title: '',
  subtitle: '',
  submitLabel: ''
})

const { t } = useI18n()
const api = useApi()

const headingText = computed(() => props.title || t('site.public_contact_title', { name: '' }).trim())
const submitText = computed(() => props.submitLabel || t('site.public_contact_submit'))

const form = reactive({
  name: '',
  email: '',
  phone: '',
  subject: '',
  message: ''
})

const loading = ref(false)
const success = ref(false)
const error = ref<string | null>(null)

const canSubmit = computed(() => form.name.trim() && form.email.trim() && form.message.trim() && !loading.value)

const submit = async () => {
  if (!canSubmit.value) return
  loading.value = true
  error.value = null
  try {
    await api.post('/api/public/contact', {
      body: {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        subject: form.subject.trim() || null,
        message: form.message.trim()
      }
    })
    success.value = true
    form.name = ''
    form.email = ''
    form.phone = ''
    form.subject = ''
    form.message = ''
  } catch (err: unknown) {
    const e = err as { data?: { errors?: Record<string, string[]>, message?: string } }
    const fieldErrors = e.data?.errors
    if (fieldErrors) {
      const first = Object.values(fieldErrors)[0]
      error.value = Array.isArray(first) ? first[0] ?? null : String(first)
    } else {
      error.value = e.data?.message ?? t('site.contact_error')
    }
  } finally {
    loading.value = false
  }
}
</script>

<template>
  <div class="rounded-2xl border border-[var(--border)] bg-[var(--bg)] p-6 sm:p-8 text-left">
    <div class="mb-6 text-center">
      <h3 class="site-display text-2xl tracking-tight">
        {{ headingText }}
      </h3>
      <p
        v-if="subtitle"
        class="mt-2 text-sm ui-text-subtle"
      >
        {{ subtitle }}
      </p>
    </div>

    <div
      v-if="success"
      class="rounded-lg border border-green-500/30 bg-green-500/10 px-4 py-6 text-center text-sm"
    >
      <UIcon
        name="i-lucide-check-circle-2"
        class="size-6 text-green-500 mx-auto mb-2"
      />
      <p class="font-medium">
        {{ t('site.contact_success_title') }}
      </p>
      <p class="mt-1 ui-text-subtle">
        {{ t('site.contact_success_body') }}
      </p>
      <button
        type="button"
        class="mt-3 text-xs underline ui-text-subtle hover:text-[var(--text)]"
        @click="success = false"
      >
        {{ t('site.contact_send_another') }}
      </button>
    </div>

    <form
      v-else
      class="space-y-4"
      @submit.prevent="submit"
    >
      <div class="grid gap-4 sm:grid-cols-2">
        <UFormField
          :label="t('site.contact_name')"
          required
        >
          <UInput
            v-model="form.name"
            :placeholder="t('site.contact_name_ph')"
            autocomplete="name"
            class="w-full"
          />
        </UFormField>
        <UFormField
          :label="t('site.contact_email')"
          required
        >
          <UInput
            v-model="form.email"
            type="email"
            :placeholder="t('site.contact_email_ph')"
            autocomplete="email"
            class="w-full"
          />
        </UFormField>
        <UFormField :label="t('site.contact_phone')">
          <UInput
            v-model="form.phone"
            :placeholder="t('site.contact_phone_ph')"
            autocomplete="tel"
            class="w-full"
          />
        </UFormField>
        <UFormField :label="t('site.contact_subject')">
          <UInput
            v-model="form.subject"
            :placeholder="t('site.contact_subject_ph')"
            class="w-full"
          />
        </UFormField>
      </div>

      <UFormField
        :label="t('site.contact_message')"
        required
      >
        <UTextarea
          v-model="form.message"
          :rows="5"
          :placeholder="t('site.contact_message_ph')"
          class="w-full"
        />
      </UFormField>

      <div
        v-if="error"
        class="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-500"
      >
        {{ error }}
      </div>

      <button
        type="submit"
        class="site-btn-accent flex w-full items-center justify-center gap-2 px-6 py-3 text-base font-semibold transition disabled:cursor-not-allowed disabled:opacity-50"
        :disabled="!canSubmit"
      >
        <UIcon
          v-if="loading"
          name="i-lucide-loader-circle"
          class="size-4 animate-spin"
        />
        {{ submitText }}
      </button>
    </form>
  </div>
</template>
